import React, { useEffect, useRef, useState } from 'react';

// App-styled replacement for window.prompt() when an action needs a password.
// Matches ConfirmDialog's look so protected actions don't drop the user into
// the browser's native "site says" box.
const PasswordDialog = ({
  open,
  title = 'Password required',
  message,
  error = '',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel
}) => {
  const [value, setValue] = useState('');
  const [show, setShow] = useState(false);
  const inputRef = useRef(null);

  // Fresh box every time it opens; keep any error the caller passed in
  useEffect(() => {
    if (open) {
      setValue('');
      setShow(false);
      // autoFocus alone loses the race with the overlay mount
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    if (value) onConfirm(value);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
      onClick={onCancel}
    >
      <form
        onSubmit={submit}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm mx-4 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-500/15">
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-gray-900 dark:text-white font-semibold text-base">{title}</h3>
            {message && <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{message}</p>}
          </div>
        </div>

        <div className="relative mt-4">
          <input
            ref={inputRef}
            type={show ? 'text' : 'password'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Password"
            autoComplete="off"
            className={`w-full px-3 py-2 pr-16 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white border focus:outline-none ${
              error
                ? 'border-red-500 focus:border-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:border-teal-500'
            }`}
          />
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-1"
          >
            {show ? 'Hide' : 'Show'}
          </button>
        </div>

        {error && <p className="text-red-500 dark:text-red-400 text-xs mt-2">{error}</p>}

        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            disabled={!value}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PasswordDialog;
